import { i18n } from 'i18n';
import { memo } from 'react';
import AssociatedItemPopover from 'src/features/edr/threat/threat-groups/ThreatListByThreats/AssociatedItemPopover';
import type {
  CellThreatGroupProps,
  HeaderThreatGroupProps,
} from 'src/features/edr/threat/threat.types';
import ThreatListHighlightedText from '../../../../common/components/ThreatListHighlightedText';
import HeaderWithTooltip from '../../../../common/components/ThreatListTable/TableHeaderWithTooltip';

export type AssociatedItemHeaderProps = {
  text: string;
  isFileDetectionType: boolean;
};

export type AssociatedItemCellProps = {
  value: string;
  query: string | undefined;
  isFileDetectionType: boolean;
  latestParentProcessName: string | undefined;
  latestParentProcessArgs: string | undefined;
  latestTargetProcessName: string | undefined;
  latestTargetProcessArgs: string | undefined;
};

export const AssociatedItemHeader = ({
  text,
  isFileDetectionType,
}: AssociatedItemHeaderProps) => {
  const tooltip = isFileDetectionType
    ? i18n.t('The most recent file name')
    : i18n.t('The most recent parent process name');

  return <HeaderWithTooltip text={text} tooltip={tooltip} />;
};

export const AssociatedItemCell = memo(
  ({
    value,
    query,
    isFileDetectionType,
    latestParentProcessName,
    latestParentProcessArgs,
    latestTargetProcessName,
    latestTargetProcessArgs,
  }: AssociatedItemCellProps) => (
    <>
      {isFileDetectionType ? (
        <ThreatListHighlightedText search={query}>
          {value}
        </ThreatListHighlightedText>
      ) : (
        <AssociatedItemPopover
          text={value}
          latestParentProcessName={latestParentProcessName}
          latestParentProcessArgs={latestParentProcessArgs}
          latestTargetProcessName={latestTargetProcessName}
          latestTargetProcessArgs={latestTargetProcessArgs}
        />
      )}
    </>
  ),
);

export const renderAssociatedItemHeader = (
  info: HeaderThreatGroupProps<string | undefined>,
  isFileDetectionType: boolean,
) => {
  const text = info.column.columnDef.meta?.displayName;
  if (!text) return null;

  return (
    <AssociatedItemHeader
      text={text}
      isFileDetectionType={isFileDetectionType}
    />
  );
};

export const renderAssociatedItemCell = (
  info: CellThreatGroupProps<string | undefined>,
  isFileDetectionType: boolean,
  query?: string,
) => {
  const value = info.getValue();
  if (!value) return '-';

  const row = info.row.original;

  return (
    <AssociatedItemCell
      value={value}
      isFileDetectionType={isFileDetectionType}
      latestParentProcessName={row.latest_parent_process_name}
      latestParentProcessArgs={row.latest_parent_process_args}
      latestTargetProcessName={row.latest_target_process_name}
      latestTargetProcessArgs={row.latest_target_process_args}
      query={query}
    />
  );
};
